import { get } from 'lodash'
import { runInAction } from 'mobx'
import rootStore from '../..'
import { wrapWithLoading } from '../../../config/general-actions'
import { responseHasError } from '../../users/actions'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import { requireMeetingById } from '../../meetings/actions'
import dayjs from 'dayjs'
import { deleteLstItem } from '../../../api/utils/api-delete'
import { getCompactLst } from '../../../api/utils/api-compact'
import { _actCheckIfListIsModified } from '../../ui-lst-sprints/actions'

const _storeKey = 'uiLstMeetings'

/**
 * @async
 * @function actLoadMeetings
 * @description Asynchronously loads meetings list from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadMeetings = wrapWithLoading(
	async () => {
		const params = {
			pg: rootStore[_storeKey].pg,
			rpg: rootStore[_storeKey].rpg,
			progress: rootStore[_storeKey].filter?.progress || ''
		}
		const _response = await getCompactLst('meetings', params)
		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Meetings' }
)

/**
 * @async
 * @function actLoadSpecificMonthMeetings
 * @description Asynchronously loads meetings for a specific month from the server and updates the UI list store.
 *
 * @param {number} timestamp - The timestamp representing the month with year for which to load meetings.
 * @param {string} type - The type of calendar panel (e.g., "month", "year").
 * @param {boolean} [get_modified=false] - A flag indicating whether to check for modifications instead
 * of loading meetings. Defaults to false.
 *
 * @returns {Promise<void|Object>} - A promise that resolves to void if meetings are loaded successfully
 * or an object indicating the result if checking for modifications.
 */
export const actLoadSpecificMonthMeetings = wrapWithLoading(
	async (timestamp, type, get_modified = false) => {
		const params = {
			timestamp: timestamp,
			type: type,
			get_modified: get_modified,

			del: rootStore[_storeKey].del,
			pg: rootStore[_storeKey].pg,
			rpg: rootStore[_storeKey].rpg,
			search: rootStore[_storeKey].search,
			progress: rootStore[_storeKey].filter?.progress || '',
			user_uid: rootStore[_storeKey].filter?.user_uid || '',
			for_user_uid: rootStore[_storeKey].filter?.for_user_uid || '',
			orderBy: rootStore[_storeKey].filter?.orderBy,
			orderDir: rootStore[_storeKey].filter?.orderDir
		}

		const _response = await getCompactLst('meetings', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success' && !get_modified) {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else if (
			!hasError &&
			_response.status === 'success' &&
			get_modified
		) {
			return _actCheckIfListIsModified(
				get(_response, ['itms'], []),
				get(_response, ['nr'], 0)
			)
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Calendar Meetings' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireMeetingById(_srvItm.id)
			_storeItm.updateFromServer(_srvItm)
		}
		rootStore[_storeKey].setIds(srvItms.map((it) => it.id))
		rootStore[_storeKey].setNr(nr)
	})
}

/**
 * @async
 * @function actUndoOrDeleteMeeting
 * @description Handles meeting deletion or undo deletion operation.
 * @param {string} meetingId - The ID of the meeting to delete/undo.
 * @param {boolean} status - Whether to delete (true) or undo (false).
 * @param {boolean} [demo=false] - Whether this is a demo operation.
 * @returns {Promise<boolean>} True if operation was successful.
 */
export const actUndoOrDeleteMeeting = wrapWithLoading(
	async (meetingId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter((id) => id !== meetingId)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})
			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['meeting'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('meeting', {
			id: meetingId,
			to_delete: timestamp || false
		})
		const _storeItm = requireMeetingById(meetingId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)
				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter((id) => id !== meetingId)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Meeting' }
)
